import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import logo from "../icons/bellhoptLogoBlack.svg";
import cartIcon from "../icons/cart.svg";
import searchIcon from "../icons/search-icon-black.svg";
import leftArrow from "../icons/leftArrow.svg";
import PRODUCT_CATEGORIES from "../constants/ProductCategories";

import { getProducts } from "../store/thunks/guestActions";
import {
  setActivePage,
  setBarOff,
  setBarOn,
  setBlurOff,
  setBlurOn,
  updateTerm,
} from "../store/slices/guestSlices";

import SaveCartButton from "./SaveCartButton";

const GuestNav = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const guestBlur = useSelector((state) => state.guestBlur);
  const { cartItems } = useSelector((state) => state.cart);
  const activeCompanyFromStore = useSelector(
    (state) => state.activeCompany.activeCompany
  );
  const { property } = useSelector((state) => state.guestLocationId || {});

  const [search, setSearch] = useState("");
  const [additionalLogoUrl, setAdditionalLogoUrl] = useState(null);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState(null);

  // Resolve active company (store or localStorage)
  let companyData = activeCompanyFromStore;
  if (!companyData || !companyData.name) {
    try {
      const stored = localStorage.getItem("activeCompany");
      if (stored) companyData = JSON.parse(stored);
    } catch {
      // ignore
    }
  }

  const companyName = companyData?.name;
  const cartItemCount =
    companyName && cartItems[companyName]
      ? cartItems[companyName].length
      : 0;

  // Cart animation
  const [cartAnimation, setCartAnimation] = useState(false);
  const [prevCartCount, setPrevCartCount] = useState(cartItemCount);

  useEffect(() => {
    if (cartItemCount > prevCartCount) {
      setCartAnimation(true);
      const t = setTimeout(() => setCartAnimation(false), 400);
      return () => clearTimeout(t);
    }
    setPrevCartCount(cartItemCount);
  }, [cartItemCount, prevCartCount]);

  // Add-on mode hides SaveCart
  const isAddOnMode =
    typeof window !== "undefined" &&
    localStorage.getItem("addonMode") === "true";

  const isCheckout = location.pathname?.includes("/checkout");
  const isDesktop =
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : false;
  const hideNav = isCheckout && !isDesktop;
  const hideSaveCart = isCheckout;

  // Fetch property-specific logo (runs when property becomes available)
  useEffect(() => {
    let mounted = true;
    let attempts = 0;

    const getLocalPropertyId = () => {
      try {
        return JSON.parse(localStorage.getItem("property") || "{}")?._id;
      } catch {
        return null;
      }
    };

    const fetchLogo = async (propertyId) => {
      try {
        const res = await axios.get(
          `https://api.bellhopt.com/api/v1/logo-by-property/${propertyId}`
        );
        const logoUrl = res?.data?.logoUrl || null;
        if (mounted) setAdditionalLogoUrl(logoUrl);
      } catch (err) {
        if (mounted) setAdditionalLogoUrl(null);
        console.error("Error fetching additional logo:", err);
      }
    };

    const tryFetch = () => {
      if (!mounted) return;

      const propertyId = property?._id || getLocalPropertyId();
      if (!propertyId) {
        attempts += 1;
        if (attempts <= 5) {
          setTimeout(tryFetch, 400);
        } else {
          setAdditionalLogoUrl(null);
        }
        return;
      }

      fetchLogo(propertyId);
    };

    tryFetch();

    return () => {
      mounted = false;
    };
  }, [property?._id]);

  const runSearch = (termInput) => {
    const trimmed = termInput.trim();
    if (!trimmed) return;

    const searchTerm = encodeURIComponent(trimmed);

    dispatch(updateTerm(trimmed));
    dispatch(getProducts({ term: searchTerm, limit: 48, start: 0 }));
    dispatch(setActivePage({ page: 1 }));

    if (location.pathname !== "/homeproducts") {
      navigate("/homeproducts");
    }

    if (props.func) {
      props.func(10);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    runSearch(search);
  };

  const handleCategorySelect = (name) => {
    setSearch(name);
    runSearch(name);
    setMobileCategoriesOpen(false);
    setFilteredCategories(null);
  };

  const handleEnterKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const scrollToCategories = () => {
    const el = document.getElementById("product-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const mobileCategories = useMemo(() => {
    const company = activeCompanyFromStore?.name || companyData?.name;
    const categories =
      PRODUCT_CATEGORIES.find((c) => c.companyName === company)?.categories ||
      [];
    return categories;
  }, [activeCompanyFromStore, companyData]);

  const closeOverlays = () => {
    dispatch(setBlurOff());
    dispatch(setBarOff());
  };

  /* ---------- Liquid Glass Property Logo ---------- */

  const PropertyLogoGlass = ({ url, variant }) => {
    if (!url) return null;

    const isDesktop = variant === "desktop";
    const maxHeightClass = isDesktop ? "max-h-[46px]" : "max-h-[40px]";
    const maxWidthClass = isDesktop ? "max-w-[200px]" : "max-w-[170px]";

    return (
      <img
        src={url}
        alt="Property logo"
        className={`
          ${maxHeightClass} ${maxWidthClass}
            object-contain
          w-full
          h-full
          block
        `}
        />
    );
  };

  /* ----------------------- RENDER ----------------------- */

  if (hideNav) return null;

  return (
    <>
      {/* Global blur overlay */}
      {guestBlur && (
        <div
          onClick={closeOverlays}
          className="fixed inset-0 z-10 bg-black/40 min-h-[650px]"
        />
      )}

      {/* MOBILE HEADER */}
      <header className="tablet:block laptop:hidden w-full bg-white guest-nav-mobile">
        <div
          className="
            w-full
            px-3 pt-3 pb-4
            flex flex-col gap-3
            fixed top-0 left-0 z-[80]
            bg-white/95
            backdrop-blur-xl
            shadow-sm
          "
        >
          {/* Top row: logos + save cart */}
          <div className="flex items-center justify-between gap-3">
            <NavLink
              to="/guest"
            className="flex items-center gap-3 min-w-0 max-w-[260px] overflow-hidden"
            >
              {/* Bellhopt logo - original size */}
              <img
                src={logo}
                alt="Bellhopt"
              className="h-[40px] w-auto block"
              />

              {/* Property logo in glass pill, same visual footprint as original */}
            {additionalLogoUrl && (
                <PropertyLogoGlass
                  url={additionalLogoUrl}
                  variant="mobile"
                className="block"
                />
            )}
            </NavLink>

            <div className="flex items-center gap-2">
              {!isAddOnMode && !hideSaveCart && <SaveCartButton size="sm" />}

              {/* (Optional) Mobile cart pill currently hidden */}
              <NavLink
                to="/cart"
                className={`
                  hidden
                  flex items-center gap-1
                  px-3 py-1.5
                  rounded-full
                  text-white
                  text-[11px] font-semibold
                  transition-all duration-400 ease-out
                  ${
                    cartAnimation
                      ? "bg-[#FF4D4D] shadow-[0_0_16px_rgba(255,77,77,0.4)] scale-[1.08]"
                      : "bg-[#FF4D4D] shadow-sm"
                  }
                `}
              >
                <img
                  src={cartIcon}
                  alt=""
                  className={`
                    w-[16px] h-[16px]
                    transition-all duration-400 ease-out
                    ${cartAnimation ? "scale-110 -rotate-3" : ""}
                  `}
                />
                <span
                  className={`
                    transition-all duration-400
                    ${cartAnimation ? "font-bold" : ""}
                  `}
                >
                  {cartItemCount}
                </span>
              </NavLink>
            </div>
          </div>

          {/* Second row: burger / back / centered search */}
          <div className="flex items-center gap-2 justify-between">
            {/* Optional back icon */}
            {props.back ? (
              <button
                onClick={() => navigate(-1)}
                className="
                  flex items-center justify-center
                  w-10 h-10
                  rounded-full
                  border border-[#E5E7EB]
                  bg-white
                "
                aria-label="Go back"
              >
                <img
                  src={leftArrow}
                  alt=""
                  className="w-4 h-4"
                />
              </button>
            ) : (
              <div className="w-10 h-10" />
            )}

            {/* Search (centered) */}
            <div className="relative flex-1 flex justify-center">
              <div className="relative w-full max-w-[420px]">
                <span className="absolute inset-y-0 left-3 flex items-center">
                  <img
                    src={searchIcon}
                    alt=""
                    className="w-4 h-4 opacity-80"
                  />
                </span>
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleEnterKey}
                  className="
                    w-full h-[40px]
                    rounded-full
                    pl-9 pr-3
                    text-[12px]
                    bg-white/98
                    border border-[#E5E7EB]
                    text-[#111827]
                    outline-none
                    text-center
                    placeholder:text-center placeholder:text-[#9CA3AF]
                  "
                />
              </div>
            </div>

            {/* Categories toggle */}
            <button
              type="button"
              onClick={() => setMobileCategoriesOpen((v) => !v)}
              className="
                flex items-center justify-center
                w-10 h-10
                rounded-full
                bg-white
                border border-[#E5E7EB]
                shadow-[0_4px_12px_rgba(15,23,42,0.08)]
                active:scale-[0.96]
                transition-all
              "
              aria-label="Browse categories"
            >
              <span className="sr-only">Categories</span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#111827"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            </button>
          </div>
        </div>

        {/* Spacer so page content isn't hidden behind fixed header */}
        <div className="h-[96px] guest-nav-mobile-spacer" />
      </header>

      {/* Mobile categories dropdown */}
      {mobileCategoriesOpen && (
        <div
          className="tablet:block laptop:hidden fixed inset-0 z-[95]"
          onClick={() => {
            setMobileCategoriesOpen(false);
            setFilteredCategories(null);
          }}
        >
          <div
            className="absolute top-[120px] left-0 right-0 px-3 pb-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="
                rounded-2xl
                bg-white
                border border-[#E5E7EB]
                shadow-[0_14px_34px_rgba(15,23,42,0.14)]
                overflow-hidden
              "
            >
              <div className="px-3 py-2 border-b border-[#E5E7EB]/80 flex items-center justify-between sticky top-0 bg-white z-10">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">
                  Shop by category
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setMobileCategoriesOpen(false);
                    setFilteredCategories(null);
                  }}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#111827] px-2 py-1 rounded-full border border-[#E5E7EB] hover:border-[#FF4D4D] hover:text-[#FF4D4D] transition-colors"
                  aria-label="Close categories"
                >
                  Close ✕
                </button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto px-3 py-3 bg-white">
                <div className="grid grid-cols-2 gap-2">
                  {(filteredCategories || mobileCategories).map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => handleCategorySelect(cat)}
                      className="
                        w-full text-left
                        rounded-xl
                        px-3 py-2.5
                        text-[12px] font-medium
                        text-[#111827]
                        bg-white
                        border border-[#E5E7EB]
                        shadow-[0_4px_12px_rgba(15,23,42,0.06)]
                        hover:border-[#FF4D4D]/60
                        hover:shadow-[0_6px_16px_rgba(15,23,42,0.08)]
                        transition-all
                      "
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DESKTOP HEADER */}
      <header
        className="
          hidden laptop:block
          fixed top-0 left-0
          w-full h-[72px]
          bg-white/92
          backdrop-blur-2xl
          shadow-[0_6px_18px_rgba(15,23,42,0.06)]
          z-[100]
        "
      >
        <div className="w-[90%] mx-auto h-full flex items-center justify-between gap-6">
          {/* Brand + property logo */}
          <NavLink
            to="/guest"
            className="flex items-center gap-4 min-w-0 max-w-[320px] overflow-hidden"
          >
            {/* Bellhopt logo - original size */}
            <img
              src={logo}
              alt="Bellhopt"
              className="h-[46px] w-auto"
            />

            {/* Property logo glass pill - original height footprint */}
            {additionalLogoUrl && (
              <PropertyLogoGlass
                url={additionalLogoUrl}
                variant="desktop"
              />
            )}
          </NavLink>

          {/* Search */}
          <div className="flex-1 flex justify-center">
            <div className="relative w-full max-w-[520px]">
              <span className="absolute inset-y-0 left-3 flex items-center">
                <img
                  src={searchIcon}
                  alt=""
                  className="w-4 h-4 opacity-75"
                />
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleEnterKey}
                placeholder="Search"
                className="
                  w-full h-[40px]
                  pl-9 pr-16
                  rounded-full
                  bg-white/98
                  border border-[#E5E7EB]
                  text-[13px]
                  text-[#111827]
                  outline-none
                  placeholder:text-[#9CA3AF]
                "
              />
              <button
                onClick={handleSearch}
                className="
                  absolute inset-y-[6px] right-2
                  px-4
                  rounded-full
                  bg-[#FF4D4D]
                  text-white
                  text-[11px] font-semibold
                  flex items-center justify-center
                  shadow-[0_6px_16px_rgba(248,113,113,0.45)]
                  hover:bg-[#E63F3F]
                  transition-colors
                "
              >
                Search
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {!isAddOnMode && !hideSaveCart && <SaveCartButton />}

            <NavLink
              to="/cart"
              className={`
                flex items-center gap-2
                px-4 py-2
                rounded-full
                text-white
                text-[12px] font-semibold
                transition-all duration-400 ease-out
                ${
                  cartAnimation
                    ? "bg-[#FF4D4D] shadow-[0_0_20px_rgba(255,77,77,0.4)] scale-[1.05]"
                    : "bg-[#FF4D4D] shadow-md"
                }
              `}
            >
              <img
                src={cartIcon}
                alt=""
                className={`
                  w-[18px] h-[18px]
                  transition-all duration-400 ease-out
                  ${cartAnimation ? "scale-110 -rotate-3" : ""}
                `}
              />
              <span
                className={`
                  transition-all duration-400
                  ${cartAnimation ? "font-bold" : ""}
                `}
              >
                {cartItemCount} item
                {cartItemCount !== 1 && "s"}
              </span>
            </NavLink>
          </div>
        </div>
      </header>

      {/* Spacer for desktop so content isn't behind fixed nav */}
      <div className="hidden laptop:block h-[72px]" />
    </>
  );
};

export default GuestNav;
